
type CachedData<T> = {
  data: T;
  expiresAt: number;
  priority: 'low' | 'medium' | 'high';
};

type CacheStats = {
  hits: number;
  misses: number;
  keys: string[];
};

class CacheService {
  private static instance: CacheService;
  private cache: Map<string, CachedData<any>> = new Map();
  
  // Default cache duration is 30 minutes
  private DEFAULT_CACHE_DURATION = 30 * 60 * 1000;
  
  // Stats tracking for debugging and optimization
  private stats: Map<string, CacheStats> = new Map();

  private constructor() {}

  public static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  /**
   * Get data from cache
   * @param key Cache key
   * @returns Cached data or null if not found/expired
   */
  public get<T>(key: string): T | null {
    const cachedItem = this.cache.get(key);
    
    if (!cachedItem) {
      this.trackMiss(key);
      return null;
    }

    // Check if cache has expired
    if (Date.now() > cachedItem.expiresAt) {
      this.cache.delete(key);
      this.trackMiss(key);
      return null;
    }

    this.trackHit(key);
    return cachedItem.data as T;
  }

  /**
   * Set data in cache
   * @param key Cache key
   * @param data Data to cache
   * @param duration Duration in milliseconds (defaults to 30 minutes)
   * @param priority Priority level for cache retention (defaults to medium)
   */
  public set<T>(
    key: string, 
    data: T, 
    duration: number = this.DEFAULT_CACHE_DURATION,
    priority: 'low' | 'medium' | 'high' = 'medium'
  ): void {
    const expiresAt = Date.now() + duration;
    this.cache.set(key, { data, expiresAt, priority });
  }

  /**
   * Clear specific cache item
   * @param key Cache key
   */
  public clear(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear entire cache or items by prefix
   * @param prefix Optional prefix to clear only matching items
   */
  public clearAll(prefix?: string): void {
    if (!prefix) {
      this.cache.clear();
      return;
    }
    
    // Clear items by prefix
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get all cached keys matching a prefix
   * @param prefix The prefix to match
   * @returns Array of matching keys
   */
  public getKeysByPrefix(prefix: string): string[] {
    return Array.from(this.cache.keys()).filter(key => key.startsWith(prefix));
  }

  /**
   * Check if cache contains unexpired data for a key
   * @param key Cache key
   * @returns Boolean indicating if valid cache exists
   */
  public has(key: string): boolean {
    const cachedItem = this.cache.get(key);
    if (!cachedItem) return false;
    return Date.now() <= cachedItem.expiresAt;
  }
  
  /**
   * Track cache hit for statistics
   * @param key Cache key that was hit
   */
  private trackHit(key: string): void {
    const category = key.split(':')[0] || 'unknown';
    const stats = this.stats.get(category) || { hits: 0, misses: 0, keys: [] };
    stats.hits++;
    if (!stats.keys.includes(key)) {
      stats.keys.push(key);
    }
    this.stats.set(category, stats);
  }
  
  /**
   * Track cache miss for statistics
   * @param key Cache key that was missed
   */
  private trackMiss(key: string): void {
    const category = key.split(':')[0] || 'unknown';
    const stats = this.stats.get(category) || { hits: 0, misses: 0, keys: [] };
    stats.misses++;
    this.stats.set(category, stats);
  }
  
  /**
   * Get cache statistics for debugging and optimization
   * @returns Map of cache statistics by category
   */
  public getStats(): Map<string, CacheStats> {
    return new Map(this.stats);
  }
  
  /**
   * Log cache statistics to console
   */
  public logStats(): void {
    console.log('Cache statistics:');
    this.stats.forEach((stats, category) => {
      const hitRate = stats.hits / (stats.hits + stats.misses) * 100 || 0;
      console.log(`Category: ${category}, Hit rate: ${hitRate.toFixed(2)}%, Hits: ${stats.hits}, Misses: ${stats.misses}, Keys: ${stats.keys.length}`);
    });
  }
  
  /**
   * Batch prefetch mechanism for optimized loading
   * @param fetcher Function to fetch data by key
   * @param keys Array of keys to prefetch
   * @param duration Cache duration
   * @param priority Cache priority
   */
  public async batchPrefetch<T>(
    fetcher: (keys: string[]) => Promise<Record<string, T>>,
    keys: string[],
    duration: number = this.DEFAULT_CACHE_DURATION,
    priority: 'low' | 'medium' | 'high' = 'medium'
  ): Promise<void> {
    // Filter out keys that are already cached
    const keysToFetch = keys.filter(key => !this.has(key));
    
    if (keysToFetch.length === 0) return;
    
    try {
      // Fetch data for all keys in one batch request
      const results = await fetcher(keysToFetch);
      
      // Cache each result individually
      Object.entries(results).forEach(([key, data]) => {
        this.set(key, data, duration, priority);
      });
    } catch (error) {
      console.error('Error in batch prefetch:', error);
    }
  }
}

export default CacheService.getInstance();
